// TransactionPassword.jsx
import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const TransactionPassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in both fields');
      return;
    }
    if (!/^\d{6}$/.test(newPassword)) {
      toast.error('Transaction password must be exactly 6 digits');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(
        '/api/users/security/transaction-password',
        {
          newPassword,
          confirmPassword,
        }
      );

      if (response.data.success) {
        toast.success('Transaction password set successfully');
        navigate('/account-details');
      } else {
        toast.error(response.data.message || 'Failed to set password');
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to set password';

      // Check if password is already set
      if (
        errorMessage.toLowerCase().includes('already set') ||
        errorMessage.toLowerCase().includes('cannot be changed')
      ) {
        toast.error(
          'Transaction password already set. Contact customer service.'
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: 'clamp(1rem, 3vw, 1.5rem)',
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      <Button
        variant='link'
        onClick={() => navigate(-1)}
        className='text-dark p-0 mb-3'
      >
        <i className='bi bi-arrow-left fs-5'></i>
      </Button>
      <h5 className='mb-3'>Set the fund password</h5>
      <Card className='p-3'>
        <Form.Group className='mb-3'>
          <Form.Label>New Password</Form.Label>
          <div className='position-relative'>
            <Form.Control
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ paddingRight: '45px' }}
            />
            <Button
              variant='link'
              className='position-absolute top-50 end-0 translate-middle-y p-0 me-2'
              onClick={() => setShowNewPassword(!showNewPassword)}
              style={{ border: 'none', background: 'none', zIndex: 10 }}
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </Button>
          </div>
        </Form.Group>
        <Form.Group className='mb-3'>
          <Form.Label>Confirm New Password</Form.Label>
          <div className='position-relative'>
            <Form.Control
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ paddingRight: '45px' }}
            />
            <Button
              variant='link'
              className='position-absolute top-50 end-0 translate-middle-y p-0 me-2'
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ border: 'none', background: 'none', zIndex: 10 }}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </Button>
          </div>
        </Form.Group>
        <Button
          className='w-100'
          style={{ backgroundColor: '#EB5757', borderColor: '#EB5757' }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Setting...' : 'Submit'}
        </Button>
      </Card>
    </Container>
  );
};

export default TransactionPassword;
